<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import StudentDetailsDialog from '@/components/Dialogs/Forms/StudentDetailsDialog.vue';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { titleCase } from '@/utils/format';
import { gradesForLevel } from '@/constants/accounts';

useSessionGuard();

// Enrolled students of the levels this admin manages, filterable by grade
// and room (the Junior/Senior High admins see their own level; the server
// applies the same limit).
const store = useStore();

const gradeOptions = gradesForLevel(store.getters.activeRoleLevel);
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/enroll-student/getStudentDataList/${year.value}`);
  return (rows || []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch(year, load, { immediate: true });

const norm = (v) => String(v ?? '').trim().toLowerCase();
const grade = shallowRef(null);
const room = shallowRef(null);
const roomOptions = computed(() => {
  const rooms = items.value
    .filter((s) => !grade.value || norm(s.grade_level) === norm(grade.value))
    .map((s) => s.room_name)
    .filter(Boolean);
  return [...new Set(rooms)].sort();
});
watch(grade, () => {
  if (room.value && !roomOptions.value.includes(room.value)) room.value = null;
});
const filtered = computed(() =>
  items.value.filter(
    (s) =>
      (!grade.value || norm(s.grade_level) === norm(grade.value)) &&
      (!room.value || norm(s.room_name) === norm(room.value)),
  ),
);

const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Grade', key: 'grade_level' },
  { title: 'Room', key: 'room_name' },
  { title: 'Status', key: 'statusEnrolled' },
];

const selected = shallowRef(null);
const detailsOpen = shallowRef(false);
const rowActions = (student) => [
  {
    title: 'View details',
    icon: 'mdi-file-document-outline',
    onClick: () => {
      selected.value = student;
      detailsOpen.value = true;
    },
  },
];
</script>

<template>
  <UiTableCard
    title="Students"
    description="Enrolled students for the selected school year."
    :headers="headers"
    :items="filtered"
    :loading="loading"
    :error="error"
    search-placeholder="Search students"
    :search-fields="['lrnNo']"
    empty-text="No students found."
    row-label="name"
    :row-actions="rowActions"
  >
    <template #toolbar>
      <v-select
        v-model="grade"
        :items="gradeOptions"
        placeholder="All grades"
        density="compact"
        hide-details
        clearable
        style="max-width: 180px"
      />
      <v-select
        v-model="room"
        :items="roomOptions"
        placeholder="All rooms"
        density="compact"
        hide-details
        clearable
        style="max-width: 200px"
      />
    </template>
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" :subtitle="item.lrnNo" />
    </template>
    <template #[`item.statusEnrolled`]="{ item }">
      <UiBadge v-if="item.statusEnrolled != null" :variant="item.statusEnrolled == 2 ? 'amber' : 'green'">
        {{ item.statusEnrolled == 2 ? 'Dropped' : 'Enrolled' }}
      </UiBadge>
    </template>
  </UiTableCard>

  <StudentDetailsDialog v-model="detailsOpen" :student="selected" @saved="load" />
</template>
